/**
 * Data catalog — knows what datasets are available and resolves queries to data sources.
 *
 * On startup, fetches catalog.json from R2 (non-blocking, background).
 * Falls back to the bundled local catalog.json if R2 is unreachable.
 */
import localCatalogData from '../../data/catalog.json';
import { R2_BASE_URL, R2_PARQUET_URL, TIMEOUT_FETCH_DEFAULT } from '../config.js';

class DataCatalog {
  constructor() {
    // Start with the bundled local catalog (always available)
    this._catalog = localCatalogData;
    this._ready = false;
    this._initPromise = this._fetchRemoteCatalog();
  }

  /**
   * Fetch catalog.json from R2 in the background.
   * If successful, replaces the local catalog with the remote one.
   */
  async _fetchRemoteCatalog() {
    try {
      const resp = await fetch(`${R2_BASE_URL}/catalog.json`, {
        signal: AbortSignal.timeout(TIMEOUT_FETCH_DEFAULT),
      });
      if (resp.ok) {
        this._catalog = await resp.json();
        console.log(`[Catalog] Remote catalog loaded: v${this._catalog.version}`);
      }
    } catch (err) {
      console.warn('[Catalog] Remote fetch failed, using local:', err.message);
    }
    this._ready = true;
  }

  /** Wait until catalog is loaded (local is instant, remote may take a moment) */
  async waitReady() {
    return this._initPromise;
  }

  /** Get the full parsed catalog object */
  getCatalog() {
    return this._catalog;
  }

  /** Get catalog version string */
  get version() {
    return this._catalog?.version || 'unknown';
  }

  /** Check if remote catalog has been loaded */
  get isReady() {
    return this._ready;
  }

  // ── Legacy API (backward compatible) ──────────────────────

  get datasets() {
    // Map old-style datasets from parquet_tables for backward compat
    const tables = this._catalog?.datasets?.parquet_tables || {};
    const mapped = {};
    for (const [key, info] of Object.entries(tables)) {
      mapped[key] = {
        name: info.description,
        description: info.description,
        files: { medium: info.r2_path },
        attributes: info.columns || [],
        queryable: true,
        supportedOperations: ['show', 'highlight', 'choropleth', 'filter', 'compare'],
      };
    }
    return mapped;
  }

  get aliases() {
    return {
      countries: ['country', 'nations', 'nation', 'sovereign'],
      states_provinces: ['states', 'provinces', 'regions', 'departments', 'prefectures', 'oblasts', 'cantons', 'territories', 'divisions', 'admin1'],
      cities: ['city', 'cities', 'towns', 'places', 'capitals', 'urban', 'populated places'],
    };
  }

  /**
   * Resolve a dataset name (or alias) to the canonical dataset key
   */
  resolveDataset(name) {
    if (!name) return null;
    const lower = name.toLowerCase();

    // Direct match
    if (this.datasets[lower]) return lower;

    // Check aliases
    for (const [key, aliasList] of Object.entries(this.aliases)) {
      if (aliasList.some(a => a.toLowerCase() === lower)) {
        return key;
      }
    }

    return null;
  }

  /**
   * Get dataset metadata
   */
  getDataset(key) {
    return this.datasets[key] || null;
  }

  /**
   * Get the file path for a dataset at a given detail level
   */
  getFilePath(datasetKey, detail = 'low') {
    const dataset = this.datasets[datasetKey];
    if (!dataset) return null;

    return dataset.files[detail] || dataset.files.medium || dataset.files.low || Object.values(dataset.files)[0];
  }

  /**
   * Check if a dataset supports a given operation
   */
  supportsOperation(datasetKey, operation) {
    const dataset = this.datasets[datasetKey];
    if (!dataset) return false;
    return dataset.supportedOperations.includes(operation);
  }

  /**
   * Get available attributes for a dataset
   */
  getAttributes(datasetKey) {
    const dataset = this.datasets[datasetKey];
    return dataset?.attributes || [];
  }

  // ── New catalog-powered APIs ──────────────────────────────

  /**
   * Get admin boundary info for a country from the catalog.
   * Returns { country_name, continent, max_depth, levels } or null.
   */
  getAdminBoundaryInfo(iso3) {
    return this._catalog?.datasets?.admin_boundaries?.[iso3] || null;
  }

  /**
   * Check if a specific format exists for a country/level in the catalog.
   * @param {string} iso3 - Country ISO-3 code
   * @param {string} admLevel - Admin level (ADM1, ADM2, etc.)
   * @param {string} format - 'geojson' | 'pmtiles' | 'geoparquet'
   */
  hasFormat(iso3, admLevel, format) {
    const info = this.getAdminBoundaryInfo(iso3);
    if (!info) return false;
    return !!info.levels?.[admLevel]?.formats?.[format];
  }

  /**
   * Get classification breaks for a country/level/metric from the catalog.
   * Returns { method, n_classes, breaks } or null.
   */
  getClassificationBreaks(iso3, admLevel, metric) {
    const info = this.getAdminBoundaryInfo(iso3);
    return info?.levels?.[admLevel]?.classification_breaks?.[metric] || null;
  }

  /**
   * Get attribute statistics for a country/level/attribute from the catalog.
   * Returns { type, min, max, mean, non_null_count } or null.
   */
  getAttributeStats(iso3, admLevel, attribute) {
    const info = this.getAdminBoundaryInfo(iso3);
    return info?.levels?.[admLevel]?.attributes?.[attribute] || null;
  }

  /**
   * Check whether a DATA-DRIVEN PMTiles render (choropleth) should be used for a
   * country/level. Requires the tiles to actually carry attributes
   * (population/density). The current Overture high-res tiles are geometry-only
   * (attributed=false), so this stays false for them — keeping choropleths and
   * poi-in-admin on their working legacy/admin_summary paths. Flips true once
   * tiles are re-generated with attributes joined in (A3b).
   */
  shouldUsePMTiles(iso3, admLevel) {
    const info = this.getAdminBoundaryInfo(iso3);
    return info?.levels?.[admLevel]?.formats?.pmtiles?.attributed === true;
  }

  /**
   * Check whether GEOMETRY PMTiles exist for a country/level (for high-res
   * whole-level OUTLINE rendering — no attributes required).
   */
  hasOutlinePMTiles(iso3, admLevel) {
    return this.hasFormat(iso3, admLevel, 'pmtiles');
  }

  /**
   * Whether the high-res GeoParquet for a country/level carries attributes
   * (population/density/area) baked in by the A3b backfill — i.e. it can drive
   * a high-res choropleth straight from the parquet.
   */
  geoparquetAttributed(iso3, admLevel) {
    const info = this.getAdminBoundaryInfo(iso3);
    return info?.levels?.[admLevel]?.formats?.geoparquet?.attributed === true;
  }

  /**
   * Get the R2 URL for a GeoParquet admin boundary file.
   * Returns the full URL or null if geoparquet is not available.
   */
  getGeoParquetUrl(iso3, admLevel) {
    const info = this.getAdminBoundaryInfo(iso3);
    const parquetInfo = info?.levels?.[admLevel]?.formats?.geoparquet;
    if (!parquetInfo?.r2_path) return null;
    return `${R2_PARQUET_URL}/${parquetInfo.r2_path}`;
  }

  /**
   * Get the feature count for a country/level from the catalog.
   */
  getFeatureCount(iso3, admLevel) {
    const info = this.getAdminBoundaryInfo(iso3);
    return info?.levels?.[admLevel]?.feature_count || 0;
  }
}

export const catalog = new DataCatalog();
export default catalog;
